/* eslint-disable react/prop-types */
import React from 'react';
import { Typography, Box, Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { isNaN } from 'lodash';
import { calculateActiveProfitLoss } from '../../../../utils/modifyScriptName';
import { setShowAccountDetails } from '../../../../redux/slices/trades';
import { dispatch } from '../../../../redux/store';
import { fNumber } from '../../../../utils/formatNumber';

const HeaderItem = ({ label, value, live }) => {
  let color = 'text.primary';
  if (live) {
    if (value > 0) {
      color = 'success.main';
    } else if (value < 0) {
      color = 'error.main';
    }
  }

  return (<Box sx={{
    display: 'flex',
    alignItems: 'center',
    py: 0.35,
    px: 0.75,
    '&:hover': {
      bgcolor: (theme) => theme.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)',
    },
  }}
  >
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        alignItems: 'center',
        gap: 0.5
      }}
    >
      <Typography
        component="span"
        sx={{
          color: 'text.secondary',
          fontSize: '0.8125rem',
          fontWeight: 500,
          minWidth: '80px',
        }}
      >
        {label}:
      </Typography>
      <Typography
        component="span"
        sx={{
          color,
          fontWeight: live ? 600 : 500,
          fontSize: '0.8125rem',
          textAlign: 'right',
          flex: 1,
        }}
      >
        {fNumber(value.toFixed(0))}
      </Typography>
    </Box>
  </Box>
  );
};

const PortfolioHeader = ({
  balance,
  bookedPL,
  brokerage,
  blockedMargin,
  activeTrades,
  priceChangeConfig,
  showAccountDetails,
}) => {
  // const dispatch = useDispatch();
  let activeProfitLoss = 0;

  if (priceChangeConfig) {
    const response = calculateActiveProfitLoss(activeTrades, priceChangeConfig);
    activeProfitLoss = response.overallProfitLoss;
  }
  if (isNaN(activeProfitLoss)) {
    activeProfitLoss = 0;
  }

  const ledgerBalance = balance + bookedPL - brokerage;
  const assetsValue = ledgerBalance + activeProfitLoss;
  const freeMargin = assetsValue - blockedMargin;

  const handleAccordionChange = (event, isExpanded) => {
    dispatch(setShowAccountDetails(isExpanded));
  };
  return (
    <Box sx={{ width: '100%', mt: -3 }}>
      <Accordion
        sx={{
          mt: 0.5,
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          '&.MuiAccordion-root': {
            '&:before': {
              display: 'none',
            },
          },
        }}
        expanded={showAccountDetails}
        onChange={handleAccordionChange}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMore sx={{ fontSize: '1.1rem' }} />}
          sx={{
            minHeight: 36,
            borderRadius: 1,
            py: 0,
            '&.Mui-expanded': {
              minHeight: 36,
              marginBottom: 0, // Remove bottom margin when expanded
            },
            '& .MuiAccordionSummary-content': {
              margin: '4px 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              '&.Mui-expanded': {
                marginBottom: 0, // Remove bottom margin of content when expanded
              }
            },
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: '0.813rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'text.primary',
            }}
          >
            Account Details
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center', bgcolor: (theme) => {
                const grossPL = (activeProfitLoss || 0) + (bookedPL || 0) - (brokerage || 0);
                const isLight = theme.palette.mode === 'light';
                const positiveColor = isLight ? 'rgba(0, 123, 85, 0.08)' : 'rgba(0, 123, 85, 0.24)';
                const negativeColor = isLight ? 'rgba(183, 33, 54, 0.08)' : 'rgba(183, 33, 54, 0.24)';
                return grossPL >= 0 ? positiveColor : negativeColor;
              },
              py: 0.75,
              px: 1.5,
              borderRadius: 1,
              mr: 3
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: '0.813rem',
                fontWeight: 700,
                letterSpacing: 0.5,
                color: () => {
                  const grossPL = (activeProfitLoss || 0) + (bookedPL || 0) - (brokerage || 0);
                  return grossPL >= 0 ? '#00684a' : '#b71d18';
                }
              }}
            >
              Gross P/L:
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: '0.875rem',
                fontWeight: 700,
                letterSpacing: 0.5,
                ml: 0.5,
                color: () => {
                  const grossPL = (activeProfitLoss || 0) + (bookedPL || 0) - (brokerage || 0);
                  return grossPL >= 0 ? '#00684a' : '#b71d18';
                }
              }}
            >
              {fNumber(((activeProfitLoss || 0) + (bookedPL || 0) - (brokerage || 0))?.toFixed(0))}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0.5, pt: 1.5 }}>
          {/* Added padding at top for spacing */}
          <Box sx={{
            bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.50' : 'grey.900',
            p: 1.25,
            borderRadius: 1,
          }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              columnGap: 1.5,
              rowGap: 0,
            }}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.2
              }}>
                <HeaderItem label="Credit" value={balance} />
                <HeaderItem label="Used Margin" value={blockedMargin} />
                <HeaderItem label="Active P/L" live value={activeProfitLoss} />
                <HeaderItem label="Free Margin" value={freeMargin} />
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                borderLeft: '1px solid rgba(145, 158, 171, 0.15)',
                pl: 2
              }}>
                <HeaderItem label="Booked P/L" value={bookedPL} />
                <HeaderItem label="Commission" value={brokerage} />
                <HeaderItem label="Net P/L" live value={bookedPL - brokerage} />
                <HeaderItem label="Avl. Balance" live value={ledgerBalance + activeProfitLoss} />
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default PortfolioHeader;
